import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useObserver } from '@/components/observer/observerContext';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import {
  Badge,
  Button,
  EntityChip,
  HoldToConfirm,
  PageHeader,
  Stat,
  StepProgress,
  Timeline,
  useToast,
  type OperationStep,
} from '@/components/ui';
import { EntityLink } from '@/components/layout/workspace/EntityLink';
import { NodeOverview } from '@/pages/design/showcase/NodeOverview';
import { timeline } from '@/pages/design/showcase/fixtures';
import { at, infraPath } from '../../model';
import { usePrefs } from '../../prefs';
import { ASK_KEY, OP_ANCHORS, OP_KEY, onSample } from '../../sampleEvents';
import { Body, Page, Panel } from '../common';
import { ObjectNotFound } from '../StateScreens';
import { NODES, isSampleIncident } from './data';

/*
  Объекты приложения-образца — открываются вкладками. Узел: экран витрины целиком (события на
  графике R29, история в плитках R25, живые имена R26) и обслуживание — длинная операция по
  шагам (R32), запуск удержанием (R27). Пока идёт операция, помощник держит «операцию» (ядро и
  выделение синеют при окраске по состоянию); по готовности — событие «готово»: вспышка зелёным и
  указание на панель (R38, spec-3). Инцидент ждёт подтверждения — «Подтвердить» снимает ожидание.
*/

const MAINTENANCE = [
  'Проверка места на приёмниках',
  'Перенос машин с узла',
  'Остановка служб',
  'Обновление ядра',
  'Перезагрузка и проверка',
];

/** Шаги обслуживания, мс; «Операция завершилась» на верхней полосе доводит идущее обслуживание до конца. */
const STEP_MS = 1400;
const MOVE_MS = 350;

function Maintenance() {
  const { notify, hold, resolve } = useObserver();
  const { toast } = useToast();
  const [step, setStep] = useState(-1);
  const [moved, setMoved] = useState(0);
  const running = step >= 0 && step < MAINTENANCE.length;

  // Готово: тост — признак на месте, помощник показывает, где это (строку читалке говорит тост).
  const finish = () => {
    setStep(MAINTENANCE.length);
    setMoved(8);
    toast({ title: 'host-338 в обслуживании: машины перенесены, ядро обновлено', tone: 'success' });
    notify({
      kind: 'done',
      key: OP_KEY,
      anchorId: OP_ANCHORS,
      label: 'host-338: обслуживание завершено',
      announce: false,
    });
  };
  const finishRef = useRef(finish);
  useEffect(() => {
    finishRef.current = finish;
  });

  // Вкладку закрыли посреди обслуживания — образец его больше не ведёт: «операция» снимается с помощника.
  useEffect(() => (running ? () => resolve(OP_KEY) : undefined), [running, resolve]);

  // Примерное событие «Операция завершилась» (sampleEvents.ts): идёт обслуживание — оно и завершается.
  useEffect(
    () =>
      onSample('done', (e) => {
        if (!running) return;
        e.preventDefault();
        finishRef.current();
      }),
    [running],
  );

  useEffect(() => {
    if (!running) return;
    if (step === 1 && moved < 8) {
      const t = window.setTimeout(() => setMoved((m) => m + 1), MOVE_MS);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => {
      if (step + 1 === MAINTENANCE.length) finishRef.current();
      else setStep(step + 1);
    }, STEP_MS);
    return () => window.clearTimeout(t);
  }, [running, step, moved]);

  const steps: OperationStep[] = MAINTENANCE.map((label, i) => ({
    id: label,
    label,
    status: step > i ? 'done' : step === i ? 'active' : 'pending',
    note: i === 1 && step >= 1 ? `${moved} из 8 машин` : undefined,
  }));

  return (
    <Panel
      title="Обслуживание узла"
      observe={OP_KEY}
      aside={
        running ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setStep(-1);
              setMoved(0);
              resolve(OP_KEY);
            }}
          >
            Остановить
          </Button>
        ) : undefined
      }
    >
      {step < 0 ? (
        <div className="flex flex-wrap items-center gap-4">
          <HoldToConfirm
            onConfirm={() => {
              setStep(0);
              hold(OP_KEY, 'update');
            }}
          >
            Перевести в обслуживание
          </HoldToConfirm>
          <span className="text-xs text-fg-muted">
            Машины переедут на соседние узлы, ядро обновится, узел перезагрузится.
          </span>
        </div>
      ) : (
        <StepProgress steps={steps} />
      )}
    </Panel>
  );
}

export function NodeScreen() {
  const { id = 'host-338' } = useParams();
  const { dense } = usePrefs();
  if (id === 'host-338') {
    return (
      <div className="flex flex-col gap-4 pb-28">
        <div className="px-8 pt-6">
          <Maintenance />
        </div>
        <NodeOverview dense={dense} />
      </div>
    );
  }
  const n = NODES.find((x) => x.id === id);
  if (!n)
    return (
      <ObjectNotFound
        title={`Узел ${id} не найден`}
        description="Его могли вывести из работы или переименовать."
        list={{ label: 'К списку узлов', path: infraPath('nodes') }}
        find="Найти узел · Ctrl+K"
      />
    );
  return (
    <Page>
      <PageHeader
        eyebrow={<EntityChip kind="location" name={n.role} size="sm" />}
        title={id}
        status={
          <Badge variant={n.state === 'Проблема' ? 'urgent' : 'success'} solid>
            {n.state}
          </Badge>
        }
        meta={<span>Гипервизор</span>}
      />
      <Body>
        <div className="grid gap-4 md:grid-cols-2">
          <Stat label="Процессор" value={String(n.cpu)} unit="%" trend={n.cpuTrend} trendFill="background" />
          <Stat label="Память" value={String(n.mem)} unit="%" />
        </div>
      </Body>
    </Page>
  );
}

export function IncidentScreen() {
  const { id = 'INC-2189' } = useParams();
  const { toast } = useToast();
  const { holds, hold, resolve } = useObserver();
  const [closed, setClosed] = useState(false);
  // Шапка — цель помощника на «Нужен ответ» (sampleEvents.ts, ASK_ANCHORS: obj.INC-2189).
  const anchorMeta = useMemo(() => ({ role: 'item' as const, label: `${id} · Load average выше 8` }), [id]);
  const headerRef = useObserverAnchor<HTMLDivElement>(`obj.${id}`, anchorMeta);
  if (!isSampleIncident(id))
    return (
      <ObjectNotFound
        title={`Проблема ${id} не найдена`}
        description="Номер мог устареть, или в нём опечатка."
        list={{ label: 'К списку проблем', path: infraPath('problems') }}
      />
    );
  return (
    <Page>
      <div ref={headerRef}>
        <PageHeader
          eyebrow={
            <>
              <EntityChip kind="location" name="Площадка А" size="sm" />
              <EntityChip kind="host" name="host-338" size="sm" />
            </>
          }
          title={`${id} · Load average выше 8`}
          status={
            closed ? (
              <Badge variant="success" solid>
                Закрыт
              </Badge>
            ) : (
              <Badge variant="urgent" solid>
                High
              </Badge>
            )
          }
          meta={
            <>
              <span>открыт 34 мин назад</span>
              <span>дежурный — Анна</span>
            </>
          }
          actions={
            closed ? undefined : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    // Ответ дан: помощник снимает ожидание (ритм уходит без жеста); «Отменить» возвращает его
                    // без полёта — событие не повторяется.
                    const waited = id === 'INC-2189' && holds[ASK_KEY] === 'asking';
                    if (waited) resolve(ASK_KEY);
                    toast({
                      title: `${id} подтверждён`,
                      action: {
                        label: 'Отменить',
                        undo: true,
                        onClick: () => {
                          if (waited) hold(ASK_KEY, 'asking');
                        },
                      },
                    });
                  }}
                >
                  Подтвердить
                </Button>
                <HoldToConfirm onConfirm={() => setClosed(true)}>Закрыть инцидент</HoldToConfirm>
              </>
            )
          }
        />
      </div>
      <Body>
        <div className="grid gap-4 xl:grid-cols-3">
          <Panel title="Что происходит" className="xl:col-span-2">
            <p className="text-sm leading-6 text-fg">
              Load average выше 8 за пять минут на{' '}
              <EntityLink
                to={at('/apps/infra/node/host-338')}
                kind="host"
                name="host-338"
                subtitle="Гипервизор · Площадка А · Proxmox 8.2"
                state={{ label: 'Проблема', variant: 'urgent' }}
                metrics={[
                  { label: 'Процессор', value: '34 %', trend: NODES[0].cpuTrend },
                  { label: 'Машин', value: '48' },
                ]}
                note="2 проблемы за сутки"
              />{' '}
              начиная с 12:13. Больше всех потребляет{' '}
              <EntityLink
                to={at('/apps/infra/node/host-338')}
                kind="vm"
                name="VM 100101"
                subtitle="Виртуальная машина · 4 ядра · 8 ГБ"
                state={{ label: 'Работает', variant: 'success' }}
                metrics={[
                  {
                    label: 'Процессор',
                    value: '96 %',
                    trend: [40, 52, 61, 70, 78, 83, 88, 91, 94, 95, 96, 96],
                  },
                ]}
              />
              . Дежурный подтвердил и смотрит.
            </p>
          </Panel>
          <Panel title="Хронология">
            <Timeline items={timeline} />
          </Panel>
        </div>
      </Body>
    </Page>
  );
}
